import React, { useState, useEffect } from "react";
import { Container, Row, Col, Button, Spinner } from "reactstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import ContractorProfile from "./ContractorProfile";
import ContractorProfileSkeleton from "./ContractorProfileSkeleton";
import LoadingOverlay from "../../Components/LoadingOverlay";
import { Contractor } from "../../models/Contractor.interface";
import { contractorAPI } from "../../services/contractorService";
import Header from "../Home/Header";
import Footer from "../Home/Footer";

const ContractorProfilePage: React.FC = () => {
  const { contractorId } = useParams<{ contractorId: string }>();
  const [contractor, setContractor] = useState<Contractor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchContractor = async () => {
      if (!contractorId) {
        setError("No contractor ID provided");
        setLoading(false);
        return;
      }

      try {
        console.log("🔍 Fetching contractor:", contractorId);
        
        // Fetch contractor from MySQL via API
        const response = await contractorAPI.getContractorById(contractorId);
        
        console.log("✅ Contractor data received:", response);

        if (!response.success || !response.data) {
          setError("Contractor not found");
          setLoading(false);
          return;
        }

        // Transform the data to match the Contractor interface
        // Cast to any to handle potential type mismatches from API response
        const apiData = response.data as any;
        
        const contractorData: Contractor = {
          id: apiData.contractorId,
          contractorId: apiData.contractorId,
          businessName: apiData.businessName,
          name: apiData.name,
          email: apiData.email,
          phone: apiData.phone,
          phone2: apiData.phone2,
          website: apiData.website,
          logo: apiData.logo,
          about: apiData.about,
          description: apiData.description,
          generalInfo: apiData.description, // Map description to generalInfo for compatibility
          slogan: apiData.slogan,
          specialOffer: apiData.specialOffer,
          rating: apiData.rating,
          reviewCount: apiData.reviewCount?.toString() || "0",
          buildzoomScore: apiData.buildzoomScore,
          bzScore: apiData.buildzoomScore, // Map for compatibility
          permitsCount: apiData.permitCount?.toString() || apiData.permitsCount?.toString(),
          profileUrl: apiData.profileUrl,
          location: apiData.location,
          neighborhood: apiData.neighborhood || [],
          address: apiData.address,
          categories: apiData.categories || [],
          services: apiData.services || [],
          servicesProducts: apiData.serviceProducts || apiData.servicesProducts || [], // Handle both possible field names
          brands: apiData.brands || [],
          accreditation: apiData.accreditations || apiData.accreditation || [], // Handle both possible field names
          searches: [], // This might need to be populated differently or set as empty
          // Additional fields that might be needed for full compatibility
          projects: apiData.projects,
          otherInformation: apiData.otherInformation,
          otherLinks: apiData.otherLinks || [],
          hours: apiData.hours,
          insurance: apiData.insurance,
          bond: apiData.bond,
          workersComp: apiData.workersComp,
          licenses: apiData.licenses || [],
          buildingPermits: apiData.buildingPermits || [],
          employees: apiData.employees || [],
          reviews: apiData.reviews || [],
          socialLinks: apiData.socialLinks,
        };

        setContractor(contractorData);
        
        // Add structured data for SEO
        addStructuredData(contractorData);
        
      } catch (err) {
        console.error("❌ Error fetching contractor:", err);
        setError("Failed to load contractor data");
      } finally {
        setLoading(false);
      }
    };

    fetchContractor();
  }, [contractorId]);

  // Function to add structured data for SEO (same as Firebase version)
  const addStructuredData = (contractor: Contractor) => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": contractor.businessName,
      "description": contractor.description || contractor.about,
      "url": contractor.website,
      "telephone": contractor.phone,
      "email": contractor.email,
      ...(contractor.address && {
        "address": {
          "@type": "PostalAddress",
          "streetAddress": contractor.address.address1,
          "addressLocality": contractor.address.city,
          "addressRegion": contractor.address.state,
          "postalCode": contractor.address.zipCode,
          "addressCountry": contractor.address.country || "US"
        }
      }),
      ...(contractor.rating && contractor.reviewCount && {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": contractor.rating,
          "reviewCount": contractor.reviewCount
        }
      }),
      ...(contractor.services && contractor.services.length > 0 && {
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Services",
          "itemListElement": contractor.services.map((service, index) => ({
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": service
            }
          }))
        }
      })
    };

    // Remove existing structured data
    const existingScript = document.getElementById('structured-data');
    if (existingScript) {
      existingScript.remove();
    }

    // Add new structured data
    let scriptTag = document.createElement('script') as HTMLScriptElement;
    scriptTag.id = 'structured-data';
    scriptTag.type = 'application/ld+json';
    document.head.appendChild(scriptTag);
    scriptTag.textContent = JSON.stringify(structuredData);
  };

  const handleBackToList = () => {
    // Check if the user came from within our site
    const referrer = document.referrer;
    const currentDomain = window.location.origin;
    
    // If there's a referrer from our domain, go back
    if (referrer && referrer.startsWith(currentDomain)) {
      navigate(-1);
    } else {
      // Otherwise, go to the contractors list
      navigate("/contractors");
    }
  };

  // Set page title based on contractor data
  useEffect(() => {
    if (contractor) {
      document.title = `${contractor.businessName} - Contractor Profile | BidBro`;
    }
  }, [contractor]);

  return (
    <React.Fragment>
      <Header isFullWidth={true} />
      <div className="page-content">
        <LoadingOverlay
                isVisible={loading}
                size="medium"
                text="BidBro"
                subtext="Loading contractor details..."
                debug={false}
              />
        <Container fluid>
          <BreadCrumb title="Contractor Profile" pageTitle="Contractors" />
          <Row>
            <Col>
              <Button color="secondary" onClick={handleBackToList}>
                ← Back
              </Button>
            </Col>
          </Row>
          <Row>
            <Col lg={12}>
              {loading ? (
                <ContractorProfileSkeleton />
              ) : error ? (
                <div className="alert alert-danger">{error}</div>
              ) : contractor ? (
                <ContractorProfile contractor={contractor} />
              ) : (
                <div className="alert alert-info">
                  No contractor data available
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default ContractorProfilePage;